import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback,
} from "react";
import axios from "axios";
import {
  getAuthToken,
  setAuthToken,
  removeAuthToken,
} from "../utils/tokenStorage";

// Use environment variable or fallback to localhost
const getApiUrl = () => {
  // Use environment variable if available
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  // Fallback for development
  if (typeof window !== "undefined") {
    const hostname = window.location.hostname;
    if (hostname === "localhost" || hostname === "127.0.0.1") {
      return "http://localhost:5001/api";
    }
    // For production without env var, use relative path
    return "/api";
  }

  return "http://localhost:5001/api";
};

// Configure axios defaults
axios.defaults.baseURL = getApiUrl();
// Note: withCredentials should NOT be set globally as it breaks CORS with external services
// It's set per-request in the interceptor instead for backend API calls only

// Add axios interceptor to include token from cookies in all requests
axios.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    const apiUrl = getApiUrl();
    const requestUrl = config.url || "";
    const isBackendRequest =
      requestUrl.startsWith("/") || requestUrl.startsWith(apiUrl);

    // Only include credentials and auth header for requests to your own backend API
    if (isBackendRequest) {
      config.withCredentials = true;
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// Add response interceptor to catch auth errors
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      console.error("🚫 401 Unauthorized:", error.config?.url);
      console.error("Request headers:", error.config?.headers);
    }
    return Promise.reject(error);
  },
);

interface User {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  role: "STUDENT" | "ADMIN" | "TEACHER" | "EMPLOYEE";
  avatar?: string;
  isVerified: boolean;
  provider?: "EMAIL" | "PHONE";
}

interface AuthState {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
}

interface AuthContextType extends AuthState {
  login: (token: string) => Promise<void>;
  logout: () => void;
  sendOTP: (identifier: string, type: "email" | "phone") => Promise<any>;
  verifyOTP: (
    identifier: string,
    otp: string,
    type: "email" | "phone",
    name?: string,
  ) => Promise<any>;
}

type AuthAction =
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "LOGIN_SUCCESS"; payload: User }
  | { type: "LOGOUT" }
  | { type: "SET_USER"; payload: User };

const initialState: AuthState = {
  user: null,
  loading: true,
  isAuthenticated: false,
};

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case "SET_LOADING":
      return { ...state, loading: action.payload };
    case "LOGIN_SUCCESS":
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        loading: false,
      };
    case "SET_USER":
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        loading: false,
      };
    case "LOGOUT":
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        loading: false,
      };
    default:
      return state;
  }
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  const loadUser = useCallback(async () => {
    try {
      const token = getAuthToken();
      // If no token exists, logout
      if (!token) {
        logout();
        return;
      }

      const response = await axios.get("/auth/me");

      if (!response.data || !response.data.user) {
        console.error("❌ loadUser: Invalid response format", response.data);
        throw new Error("Invalid user data received");
      }

      dispatch({ type: "SET_USER", payload: response.data.user });
    } catch (error: any) {
      console.error("❌ loadUser error:", error);
      console.error("Response data:", error.response?.data);
      console.error("Response status:", error.response?.status);
      console.error("Request URL:", error.config?.url);
      console.error("Request baseURL:", error.config?.baseURL);
      // If token is invalid, clear it and logout
      removeAuthToken();
      delete axios.defaults.headers.common["Authorization"];
      dispatch({ type: "LOGOUT" });
      throw error; // Re-throw so login function catches it
    }
  }, []);

  // Configure axios to include token in requests
  useEffect(() => {
    const token = getAuthToken();
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      loadUser();
    } else {
      dispatch({ type: "SET_LOADING", payload: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-validate session when page becomes visible (tab switching on mobile)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && state.isAuthenticated) {
        // Page is visible again, re-validate the session
        const token = getAuthToken();
        if (token) {
          loadUser().catch(() => {
            // If validation fails, logout
            console.warn("Session validation failed, logging out");
            logout();
          });
        } else {
          // Token was removed, logout
          logout();
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [state.isAuthenticated, loadUser]);

  const login = async (token: string) => {
    try {
      // First, ensure clean state by removing any existing token
      removeAuthToken();
      delete axios.defaults.headers.common["Authorization"];

      // Now set the new token with proper security attributes
      setAuthToken(token);
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

      await loadUser();
    } catch (error) {
      throw error;
    }
  };
  const logout = () => {
    // Remove cookie with same path attribute
    removeAuthToken();
    delete axios.defaults.headers.common["Authorization"];

    // Clear any localStorage items (defensive cleanup)
    try {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
    } catch (e) {
      console.warn("Could not clear localStorage:", e);
    }

    // Clear sessionStorage as well
    try {
      sessionStorage.clear();
    } catch (e) {
      console.warn("Could not clear sessionStorage:", e);
    }

    dispatch({ type: "LOGOUT" });
  };

  const sendOTP = async (identifier: string, type: "email" | "phone") => {
    try {
      const response = await axios.post("/auth/send-otp", {
        identifier,
        type,
      });
      return response.data;
    } catch (error: any) {
      throw error.response?.data || error;
    }
  };

  const verifyOTP = async (
    identifier: string,
    otp: string,
    type: "email" | "phone",
    name?: string,
  ) => {
    try {
      const response = await axios.post("/auth/verify-otp", {
        identifier,
        otp,
        type,
        name,
      });

      if (response.data.token) {
        await login(response.data.token);
      }

      return response.data;
    } catch (error: any) {
      throw error.response?.data || error;
    }
  };

  const value: AuthContextType = {
    ...state,
    login,
    logout,
    sendOTP,
    verifyOTP,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
